import type { BaseRange } from 'slate'
import type { RefObject } from 'react'
import { useContext } from 'react'
import { useSlateStatic } from 'slate-react'

import { Popover } from '../../../../../Popover'
import type { PopoverSide } from '../../../../../Popover/types'
import { ToolbarContext } from '../../text/RichTextEditor'
import { getLink, isLinkFocused, unlink } from '../../text/links'
import { handleInsertLinkCommon, useLinkDetailsEffect } from './LinkTooltip.utils'
import { useTooltipState } from './LinkTooltipContext'
import EditMode from './components/EditMode'
import ViewMode from './components/ViewMode'
import useTooltipManagement from '../../text/useTooltipManagement'

export default function LinkTooltipText({
  sideOffset,
  side,
  editableRef: _onlyUsedToForceARerender,
}: {
  readonly sideOffset?: number
  readonly side?: PopoverSide
  readonly editableRef?: RefObject<HTMLElement | null>
}) {
  const editor = useSlateStatic()
  const { linkTooltipState, linkTooltipDispatch, errorDispatch } = useTooltipState()
  const [{ isLinkTooltipOpen: isOpen }, toolbarDispatch] = useContext(ToolbarContext)
  const lastSelection: RefObject<BaseRange | undefined> = editor.useLastSelection(editor)

  const [inputRef, virtualReferenceElement] = useTooltipManagement()

  useLinkDetailsEffect(
    editor,
    isOpen,
    linkTooltipState,
    linkTooltipDispatch,
    toolbarDispatch,
    isLinkFocused,
    getLink,
    'open_link_tooltip',
    'close_link_tooltip',
  )

  function handleInsertLink() {
    handleInsertLinkCommon(editor, lastSelection, linkTooltipState, linkTooltipDispatch, errorDispatch, false)
  }

  const virtualRef = {
    current: virtualReferenceElement,
  } as React.RefObject<HTMLElement>

  return (
    <Popover
      open={isOpen}
      virtualRef={virtualRef}
      side={side}
      className={'dali-plugin-link-tooltip'}
      usePortal
      avoidCollisions
      sideOffset={sideOffset}
      onMouseDown={(e) => {
        e.stopPropagation()
      }}
      onPointerDownOutside={() => {
        toolbarDispatch({ type: 'close_link_tooltip' })
      }}
      onOpenAutoFocus={(e) => {
        if (linkTooltipState.isViewMode) {
          e.preventDefault()
        }
      }}
    >
      {linkTooltipState.isViewMode ? (
        <ViewMode lastSelection={lastSelection} editor={editor} unlink={unlink} />
      ) : (
        <EditMode inputRef={inputRef} handleInsert={handleInsertLink} />
      )}
    </Popover>
  )
}
